import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import ContactModal from './ContactModal';
import PageHeader from '../common/PageHeader';
import PageState from '../common/PageState';
import { Button } from '../../styles/common/Button.styled';
import * as T from '../../styles/common/table.styled';
import { formatPhoneNumber, getCategoryLabel } from '../../utils/format';
import { isTableRowActionTarget } from '../../utils/tableInteraction';
import Pagination from '../common/Pagination';
import DebouncedSearchInput from '../common/DebouncedSearchInput';
import { useAuth } from '../../contexts/AuthContext';

const formatCallDuration = (seconds: number | null | undefined) => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '-';
  const roundedSeconds = Math.round(seconds);
  const minutes = Math.floor(roundedSeconds / 60);
  return minutes > 0 ? `${minutes}분 ${roundedSeconds % 60}초` : `${roundedSeconds}초`;
};

const formatLatestCallAt = (dateTime: string | null | undefined) => {
  if (!dateTime) return '통화 없음';
  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isPhoneSearch = (value: string) => /^[0-9\s()+-]+$/.test(value) && /\d/.test(value);

type ContactSortOption = { value: string; orderType: string; orderAsc: 'ASC' | 'DESC'; label: string };
const BASIC_SORT_OPTIONS: ContactSortOption[] = [
  { value: 'nameAsc', orderType: 'NAME', orderAsc: 'ASC', label: '고객명' },
  { value: 'name', orderType: 'NAME', orderAsc: 'DESC', label: '고객명' },
  { value: 'avgCallTime', orderType: 'AVG_CALL_TIME', orderAsc: 'DESC', label: '평균 통화시간' },
  { value: 'avgCallTimeAsc', orderType: 'AVG_CALL_TIME', orderAsc: 'ASC', label: '평균 통화시간' },
  { value: 'latestCallAt', orderType: 'LATEST_CALL_AT', orderAsc: 'DESC', label: '최근 통화' },
  { value: 'latestCallAtAsc', orderType: 'LATEST_CALL_AT', orderAsc: 'ASC', label: '최근 통화' },
  { value: 'totalCallCount', orderType: 'TOTAL_CALL_COUNT', orderAsc: 'DESC', label: '총 통화' },
  { value: 'totalCallCountAsc', orderType: 'TOTAL_CALL_COUNT', orderAsc: 'ASC', label: '총 통화' },
];
const CORE_SORT_OPTIONS = BASIC_SORT_OPTIONS.slice(0, 2);
const getContactSortOption = (value: string, isCore: boolean) => {
  const options = isCore ? CORE_SORT_OPTIONS : BASIC_SORT_OPTIONS;
  return options.find(option => option.value === value) || options[0];
};

const SortableHeaderButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 2px; }
  span { color: ${({ theme, $active }) => $active ? theme.color.point_y_100 : theme.color.text_color_400}; font-size: 13px; }
`;

const ContactList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error, request } = useApi();
  const { mode } = useAuth();
  const isCoreMode = mode === 'core';

  // URL 파라미터에서 현재 상태 추출
  const currentPage = parseInt(searchParams.get('pg') || '1');
  const appliedPhone = searchParams.get('phone') || '';
  const appliedName = searchParams.get('name') || '';
  const appliedSort = searchParams.get('sort') || '';
  const sortOption = getContactSortOption(appliedSort, isCoreMode);

  // 검색 버튼 클릭 전까지는 URL에 반영하지 않고, 검색어 종류는 입력값으로 판단한다.
  const [searchQuery, setSearchQuery] = useState(appliedName || appliedPhone);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 뒤로가기 등으로 URL이 변경될 때 검색 필드 동기화
  useEffect(() => {
    setSearchQuery(appliedName || appliedPhone);
  }, [appliedPhone, appliedName]);

  const fetchContacts = useCallback(async (page: number, phone: string, name: string, sortValue: string) => {
    const normalizedPhone = phone ? phone.replace(/\D/g, '') : undefined;
    const sort = getContactSortOption(sortValue, mode === 'core');
    await request({
      url: '/contact/list',
      method: 'GET',
      params: {
        pg: page,
        limit: 10,
        phone: normalizedPhone || undefined,
        name: name || undefined,
        orderType: sort.orderType,
        orderAsc: sort.orderAsc,
      },
    });
  }, [mode, request]);

  useEffect(() => {
    fetchContacts(currentPage, appliedPhone, appliedName, appliedSort);
  }, [currentPage, appliedPhone, appliedName, appliedSort, fetchContacts, mode]);

  const buildSearchParams = useCallback((page: number, phone: string, name: string, sortValue = appliedSort) => {
    const params: Record<string, string> = { pg: page.toString() };
    if (phone) params.phone = phone;
    if (name) params.name = name;
    const sort = getContactSortOption(sortValue, isCoreMode);
    if (sort.value !== getContactSortOption('', isCoreMode).value) params.sort = sort.value;
    return params;
  }, [appliedSort, isCoreMode]);

  const canAutoSearch = useCallback((query: string) => {
    if (!query) return true;
    if (isPhoneSearch(query)) return query.replace(/\D/g, '').length >= 3;
    return true;
  }, []);

  const handleSearch = useCallback((nextQuery?: string) => {
    const query = (typeof nextQuery === 'string' ? nextQuery : searchQuery).trim();
    const params = buildSearchParams(1, isPhoneSearch(query) ? query : '', isPhoneSearch(query) ? '' : query);
    setSearchParams(params);
  }, [buildSearchParams, searchQuery, setSearchParams]);

  const handlePageChange = useCallback((page: number) => {
    setSearchParams(buildSearchParams(page, appliedPhone, appliedName));
  }, [appliedName, appliedPhone, buildSearchParams, setSearchParams]);

  const handleSort = useCallback((orderType: string) => {
    const options = isCoreMode ? CORE_SORT_OPTIONS : BASIC_SORT_OPTIONS;
    const current = getContactSortOption(appliedSort, isCoreMode);
    const nextAsc = current.orderType === orderType && current.orderAsc === 'DESC' ? 'ASC' : 'DESC';
    const next = options.find(option => option.orderType === orderType && option.orderAsc === nextAsc) || options[0];
    setSearchParams(buildSearchParams(1, appliedPhone, appliedName, next.value));
  }, [appliedName, appliedPhone, appliedSort, buildSearchParams, isCoreMode, setSearchParams]);

  const sortButtonLabel = (label: string, orderType: string) => {
    if (sortOption.orderType !== orderType) return `${label} 기준 정렬`;
    return `${label} 기준 정렬, 현재 ${sortOption.orderAsc === 'ASC' ? '오름차순' : '내림차순'}`;
  };

  const contacts = data?.content || [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements || 0;

  return (
    <T.PageContainer>
      <PageHeader title="연락처 관리" count={data && !loading && !error ? totalElements : undefined} actions={!isCoreMode && (
          <Button

            onClick={() => setIsModalOpen(true)}
          >
            주소록 등록
          </Button>
        )} />
      <T.TopBar>
        <T.SearchGroup>
          <T.SearchInput as={DebouncedSearchInput}
            aria-label="전화번호 또는 이름 검색" placeholder="전화번호 또는 이름 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSearch={handleSearch}
            delay={250}
            shouldSearch={canAutoSearch}
            style={{ width: '280px' }}
          />
          <T.SearchButton onClick={() => handleSearch()}>검색</T.SearchButton>
        </T.SearchGroup>

      </T.TopBar>



      <T.TableArea $stickyActions $minWidth={isCoreMode ? 640 : 1170}>
        <T.Table>
          <colgroup>
            {!isCoreMode && <col style={{ width: '60px' }} />}
            <col style={{ width: '150px' }} />
            <col style={{ width: '200px' }} />
            {!isCoreMode && <col style={{ width: '120px' }} />}
            {!isCoreMode && <col style={{ width: '120px' }} />}
            {!isCoreMode && <col style={{ width: '160px' }} />}
            {!isCoreMode && <col style={{ width: '100px' }} />}
            {!isCoreMode && <col style={{ width: '140px' }} />}
            <col style={{ width: '140px' }} />
          </colgroup>
          <thead>
            <tr>
              {!isCoreMode && <T.Th>No.</T.Th>}
              <T.Th>전화번호</T.Th>
              <T.Th aria-sort={sortOption.orderType === 'NAME' ? sortOption.orderAsc === 'ASC' ? 'ascending' : 'descending' : 'none'}>
                <SortableHeaderButton $active={sortOption.orderType === 'NAME'} aria-label={sortButtonLabel('고객명', 'NAME')} onClick={() => handleSort('NAME')}>
                  고객명 <span aria-hidden="true">{sortOption.orderType === 'NAME' ? sortOption.orderAsc === 'ASC' ? '↑' : '↓' : '↕'}</span>
                </SortableHeaderButton>
              </T.Th>
              {!isCoreMode && <T.Th>카테고리</T.Th>}
              {!isCoreMode && <T.Th>등록일</T.Th>}
              {!isCoreMode && <T.Th aria-sort={sortOption.orderType === 'LATEST_CALL_AT' ? sortOption.orderAsc === 'ASC' ? 'ascending' : 'descending' : 'none'}><SortableHeaderButton $active={sortOption.orderType === 'LATEST_CALL_AT'} aria-label={sortButtonLabel('최근 통화', 'LATEST_CALL_AT')} onClick={() => handleSort('LATEST_CALL_AT')}>최근 통화 <span aria-hidden="true">{sortOption.orderType === 'LATEST_CALL_AT' ? sortOption.orderAsc === 'ASC' ? '↑' : '↓' : '↕'}</span></SortableHeaderButton></T.Th>}
              {!isCoreMode && <T.Th aria-sort={sortOption.orderType === 'TOTAL_CALL_COUNT' ? sortOption.orderAsc === 'ASC' ? 'ascending' : 'descending' : 'none'}><SortableHeaderButton $active={sortOption.orderType === 'TOTAL_CALL_COUNT'} aria-label={sortButtonLabel('총 통화', 'TOTAL_CALL_COUNT')} onClick={() => handleSort('TOTAL_CALL_COUNT')}>총 통화 <span aria-hidden="true">{sortOption.orderType === 'TOTAL_CALL_COUNT' ? sortOption.orderAsc === 'ASC' ? '↑' : '↓' : '↕'}</span></SortableHeaderButton></T.Th>}
              {!isCoreMode && <T.Th aria-sort={sortOption.orderType === 'AVG_CALL_TIME' ? sortOption.orderAsc === 'ASC' ? 'ascending' : 'descending' : 'none'}><SortableHeaderButton $active={sortOption.orderType === 'AVG_CALL_TIME'} aria-label={sortButtonLabel('평균 통화시간', 'AVG_CALL_TIME')} onClick={() => handleSort('AVG_CALL_TIME')}>평균 통화시간 <span aria-hidden="true">{sortOption.orderType === 'AVG_CALL_TIME' ? sortOption.orderAsc === 'ASC' ? '↑' : '↓' : '↕'}</span></SortableHeaderButton></T.Th>}
              <T.Th>관리</T.Th>
            </tr>
          </thead>
          <tbody>
            {loading || (!data && !error) ? (
              <T.Tr>
                  <T.Td colSpan={isCoreMode ? 3 : 9} style={{ textAlign: 'center', color: 'inherit' }}>로딩 중...</T.Td>
              </T.Tr>
            ) : error ? <T.Tr><T.Td colSpan={isCoreMode ? 3 : 9}><PageState status="error" onRetry={() => fetchContacts(currentPage, appliedPhone, appliedName, appliedSort)} /></T.Td></T.Tr> : contacts.length > 0 ? (
              contacts.map((contact: any, index: number) => (
                <T.Tr
                  key={contact.id}
                  $clickable
                  onDoubleClick={(event) => {
                    if (isTableRowActionTarget(event.target)) return;
                    navigate(`/contact/${contact.id}`);
                  }}
                >
                  {!isCoreMode && <T.Td>{totalElements - (currentPage - 1) * 10 - index}</T.Td>}
                  <T.Td>{formatPhoneNumber(contact.phone)}</T.Td>
                  <T.Td>{contact.name}</T.Td>
                  {!isCoreMode && <T.Td>{getCategoryLabel(contact.category)}</T.Td>}
                  {!isCoreMode && <T.Td>{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '-'}</T.Td>}
                  {!isCoreMode && <T.Td>{formatLatestCallAt(contact.latestCallAt)}</T.Td>}
                  {!isCoreMode && <T.Td>{contact.totalCallCount == null ? '-' : `${contact.totalCallCount}회`}</T.Td>}
                  {!isCoreMode && <T.Td>{formatCallDuration(contact.avgCallTime)}</T.Td>}
                  <T.Td>
                    <T.ActionButton onClick={() => navigate(`/contact/${contact.id}`)}>
                      상세보기
                    </T.ActionButton>
                  </T.Td>
                </T.Tr>
              ))
            ) : (
              <T.Tr>
                <T.Td colSpan={isCoreMode ? 3 : 9}>
                  <T.NoData>{appliedPhone || appliedName ? '검색 조건에 맞는 연락처가 없습니다.' : '등록된 주소록이 없습니다.'}</T.NoData>
                </T.Td>
              </T.Tr>
            )}
          </tbody>
        </T.Table>
      </T.TableArea>

      <T.TableFooter>{data && !loading && !error && <T.TotalCount>총 <span>{totalElements}</span>건</T.TotalCount>}<Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      /></T.TableFooter>

      {!isCoreMode && (
        <ContactModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => fetchContacts(currentPage, appliedPhone, appliedName, appliedSort)}
          mode="create"
        />
      )}
    </T.PageContainer>
  );
};

export default ContactList;
